import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../../sporttia/all';
import {
    updateElementInArray,
    deleteElementFromArray,
    subsetObject,
} from '../../../lib/utils';
import { useInteractionsFiles } from '../../../lib/hooks';
import { usePermission } from '../../../lib/usePermission';
import { PERMISSIONS } from '../../../lib/permissions';

const type = 'SALE';

type UseSalePointParams = {
    idTicket: string | number;
    onClose: () => void;
};

export type SalePointState = {
    cart: any[];
    mship: any;
    setMship: (mship: any) => void;
    ticket: any;
    setTicket: (ticket: any) => void;
    allProducts: any[] | undefined;
    setAllProducts: (products: any[] | undefined) => void;
    /** Sólo lectura: ticket cerrado, o el operador no puede vender. */
    readOnly: boolean;
    /** Puede crear, editar y borrar productos y su stock. */
    canManageProducts: boolean;
    /** Puede anular el ticket. */
    canDeleteTicket: boolean;
    previousPurchaseTicket: any;
    totalNet: number;
    totalVat: number;
    total: number;
    onSelectProduct: (product: any) => void;
    saveMovementInCart: (mov: any) => void;
    updateProduct: (product: any) => void;
    deleteProduct: (product: any) => void;
    downloadTicket: () => void;
    deleteTicket: () => void;
    reset: (closedProductTicket: any) => void;
    /** Cierra, borrando antes el ticket en curso si está vacío. */
    requestClose: () => void;
};

/**
 * Toda la lógica del punto de venta: ticket, carrito, productos y totales. Vive
 * aparte de la pantalla porque el punto de venta se pinta de dos formas — como
 * página en su propia URL y como diálogo al abrir un ticket ya existente desde
 * el listado — y ninguna de las dos debe cargar con la otra.
 */
export function useSalePoint({
    idTicket,
    onClose,
}: UseSalePointParams): SalePointState {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { openFile } = useInteractionsFiles();
    const { can } = usePermission();
    const canSell = can(PERMISSIONS.SALE_POINT.CREATE);
    const canManageProducts = can(PERMISSIONS.SALE_POINT.MODIFY);
    const [cart, setCart] = useState<any[]>([]);
    const [mship, setMship] = useState<any>({ fullName: '' });
    const [ticket, setTicket] = useState<any>();
    // The API only deletes OPEN tickets (869fkk88w): a closed sale cannot be voided here.
    const canDeleteTicket =
        can(PERMISSIONS.SALE_POINT.DELETE) && ticket?.status === 'OPEN';
    const [allProducts, setAllProducts] = useState<any[] | undefined>([]);
    const [readOnly, setReadOnly] = useState<boolean>(true);
    const [previousPurchaseTicket, setPreviousPurchaseTicket] =
        useState<any>(null);

    // Totals
    const [totalNet, setTotalNet] = useState<number>(0);
    const [totalVat, setTotalVat] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);

    const loadAllProducts = () => {
        cxt?.api('GET', `/scs/${cxt?.sc?.id}/products`, {
            params: {
                rows: 500,
            },
            success: (r: any) => {
                // Product with no category, category = 'Productos'
                const products = r.rows.map((product: any) => ({
                    ...product,
                    category: product.category ? product.category : t('Others'),
                }));

                setAllProducts(products ?? []);
            },
        });
    };

    const loadTicket = () => {
        cxt?.api('GET', `/products/tickets/${idTicket}`, {
            success: (r: any) => {
                setTicket(r.productTicket);
                setReadOnly(r.productTicket.status === 'CLOSE');
                if (r.productTicket.status === 'CLOSE') {
                    setPreviousPurchaseTicket(r.productTicket);
                }
                if (r.productTicket.movs) {
                    setCart(r.productTicket.movs);
                }
            },
        });
    };

    const createNewTicket = () => {
        // Sin permiso de vender no se abre un ticket que no se podrá cobrar
        if (!canSell) return;

        cxt?.api('POST', `/scs/${cxt?.sc?.id}/products/tickets`, {
            params: {
                type,
                idUser: mship?.user?.id,
            },
            success: (r: any) => {
                setTicket(r.productTicket);
                setReadOnly(false);
            },
        });
    };

    const downloadTicket = () => {
        if (previousPurchaseTicket.payment) {
            openFile(
                `/payments/${previousPurchaseTicket.payment.id}.pdf`,
                'application/pdf',
                null,
                `${t('Ticket')}_${previousPurchaseTicket.payment.id}`,
            );
        }
    };

    const deleteTicket = () => {
        cxt?.api('DELETE', `/products/tickets/${ticket.id}`, {
            success: () => {
                onClose();
            },
        });
    };

    const updateProduct = (product: any) => {
        setAllProducts(updateElementInArray(allProducts ?? [], product));
    };

    const deleteProduct = (product: any) => {
        setAllProducts(
            (allProducts ?? []).filter(
                (currentProduct) => currentProduct?.id !== product?.id,
            ),
        );
    };

    /**
     * Create/update movement in cart
     */
    const saveMovementInCart = (mov: any) => {
        const params = subsetObject(mov, 'amount,concept,netPrice,vat');

        // Product is in the cart already -> update movement
        if (mov.id) {
            cxt?.api('PUT', `/products/movs/${mov.id}`, {
                params,
                success: (r: any) => {
                    // Final amount was 0 then the mov has been deleted by backend
                    if (r.productMov.trash) {
                        // Delete product from cart
                        setCart(
                            deleteElementFromArray(cart, r.productMov) ?? [],
                        );
                    } else {
                        // Update cart & product (stock)
                        setCart(updateElementInArray(cart, r.productMov) ?? []);
                    }

                    updateProduct(r.product);
                },
            });

            // Product new in the cart -> create movement
        } else {
            cxt?.api('POST', `/products/tickets/${ticket.id}/movs`, {
                params: {
                    ...params,
                    idProduct: mov.product.id,
                },
                success: (r: any) => {
                    // Update cart & product (stock)
                    setCart(updateElementInArray(cart, r.productMov) ?? []);
                    updateProduct(r.product);
                },
            });
        }
    };

    const onSelectProduct = (product: any) => {
        // Find the movement just in case the product is already in the cart
        const foundMov = cart.find((mov: any) => mov.product.id === product.id);
        // Si seleccionamos un nuevo producto, reseteamos el anterior ticket, ya no se podrá imprimir
        setPreviousPurchaseTicket(null);
        // Create new movement with amount -1
        saveMovementInCart({
            id: foundMov && foundMov.id,
            amount: (foundMov ? foundMov.amount : 0) - 1,
            product,
        });
    };

    const reset = (closedProductTicket: any) => {
        // Empty cart
        setCart([]);

        // It's on creating mode
        if (idTicket === 'create') {
            // Guardamos el ticket que se ha cerrado para que se pueda imprimir y no se sobreescriba con el nuevo
            setPreviousPurchaseTicket(closedProductTicket);
            createNewTicket();
        }
    };

    /**
     * Before close, delete if it's empty
     */
    const requestClose = () => {
        // Cart empty
        if (cart.length === 0 && ticket?.status === 'OPEN') {
            deleteTicket();
        } else {
            onClose();
        }
    };

    /**
     * Ticket id changes
     */
    useEffect(() => {
        // Only create if we are not loading one
        if (idTicket) {
            if (idTicket === 'create') {
                createNewTicket();
            } else {
                loadTicket();
            }
        }
    }, [idTicket]);

    /**
     * INIT - Just opened
     */
    useEffect(() => {
        // Load all products
        loadAllProducts();

        // Get sports center to get the guest user
        cxt?.api('GET', `/scs/${cxt?.sc?.id}`, {
            success: (r: any) => {
                // Set guest user
                if (r.sc.guestUser && r.sc.guestUser.mship) {
                    setMship({
                        id: r.sc.guestUser.mship.id,
                        fullName: r.sc.guestUser.mship.fullName,
                        user: {
                            id: r.sc.guestUser.id,
                        },
                    });
                }
            },
        });
    }, []);

    /**
     * Calculate totals
     */
    useEffect(() => {
        let calcTotal = 0;
        let calcTotalNet = 0;
        let calcTotalVat = 0;
        cart.forEach((mov: any) => {
            calcTotal += mov.finalPrice * mov.amount;
        });
        if (cart.length > 0) {
            calcTotalNet = calcTotal / (1 + cart[0].vat / 100);
            calcTotalVat = calcTotal - calcTotalNet;
        }

        setTotalNet(Math.abs(calcTotalNet));
        setTotalVat(Math.abs(calcTotalVat));
        setTotal(Math.abs(calcTotal));
    }, [cart]);

    return {
        cart,
        mship,
        setMship,
        ticket,
        setTicket,
        allProducts,
        setAllProducts,
        readOnly: readOnly || !canSell,
        canManageProducts,
        canDeleteTicket,
        previousPurchaseTicket,
        totalNet,
        totalVat,
        total,
        onSelectProduct,
        saveMovementInCart,
        updateProduct,
        deleteProduct,
        downloadTicket,
        deleteTicket,
        reset,
        requestClose,
    };
}
